import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Star, Trophy, Camera, Headphones, HelpCircle, Sparkles, Gift, Target, Zap } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { getActiveTrip } from '@/lib/api';
import { toast } from 'sonner';
import confetti from 'canvas-confetti';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * Dashboard Kids - Vue Enfant 6-12 ans
 * - UI ludique et colorée
 * - Gamification (points, badges, animations)
 * - Itinéraire visuel (images, gros texte)
 * - Audio guides kids + Quiz
 * - Photo challenges
 * - Animations et confettis
 */
export default function KidsDashboard() {
  const navigate = useNavigate();
  const [activeTrip, setActiveTrip] = useState(null);
  const [points, setPoints] = useState(45);
  const [badges, setBadges] = useState(3);
  const [todayActivities, setTodayActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPointsGain, setShowPointsGain] = useState(null);
  const [dailyObjectives, setDailyObjectives] = useState([
    { id: 1, label: '🎧 Écouter 1 guide audio', done: false, points: 10 },
    { id: 2, label: '❓ Faire 2 quiz', done: true, points: 10 },
    { id: 3, label: '📸 Prendre 3 photos', done: false, points: 15 },
  ]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const activeTripRes = await getActiveTrip();
      setActiveTrip(activeTripRes?.data);
      
      // Mock data
      setTodayActivities([
        { time: '10h', title: 'Musée du Louvre', emoji: '🏛️', color: 'bg-purple-100' },
        { time: '14h', title: 'Goûter', emoji: '🍰', color: 'bg-pink-100' },
        { time: '19h', title: 'Tour Eiffel', emoji: '🗼', color: 'bg-yellow-100' }
      ]);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  // 🎉 Celebrate points gain with confetti
  const celebratePoints = (pointsGained) => {
    setShowPointsGain(pointsGained);
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#FFD700', '#FFA500', '#FF69B4', '#9370DB']
    });
    setTimeout(() => setShowPointsGain(null), 2000);
  };

  // Handle game card click with animation
  const handleGameClick = (path, pointsToAdd = 0) => {
    if (pointsToAdd > 0) {
      setPoints(prev => prev + pointsToAdd);
      celebratePoints(pointsToAdd);
    }
    setTimeout(() => navigate(path), 300);
  };

  if (loading) {
    return <div className="h-48 bg-gradient-to-br from-purple-200 to-pink-200 rounded-3xl animate-pulse" />;
  }

  return (
    <div className="space-y-6 p-6 bg-gradient-to-br from-purple-50 via-pink-50 to-yellow-50 min-h-screen relative">
      {/* Floating points gain animation */}
      <AnimatePresence>
        {showPointsGain && (
          <motion.div
            initial={{ opacity: 0, y: 100, scale: 0.5 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -100, scale: 0.5 }}
            className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-50"
          >
            <div className="bg-gradient-to-r from-yellow-400 to-orange-500 text-white px-8 py-6 rounded-3xl shadow-2xl text-center">
              <Sparkles className="w-12 h-12 mx-auto mb-2 animate-spin" />
              <p className="text-4xl font-black">+{showPointsGain}</p>
              <p className="text-xl font-bold">Points ! 🎉</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Header Colorful */}
      <motion.div 
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        className="text-center"
      >
        <h1 className="text-3xl font-black text-transparent bg-clip-text bg-gradient-to-r from-purple-600 to-pink-600">
          🎉 Voyage à {activeTrip?.destination || 'Paris'} !
        </h1>
        <div className="h-2 bg-gradient-to-r from-purple-500 via-pink-500 to-yellow-500 rounded-full mt-3 mx-auto w-32 animate-pulse" />
      </motion.div>

      {/* Daily Objectives - NEW */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.1 }}
      >
        <Card className="border-4 border-green-300 shadow-xl bg-gradient-to-br from-green-50 to-emerald-50">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-green-700 mb-4 flex items-center gap-2">
              🎯 Objectifs du Jour
            </h2>
            <div className="space-y-3">
              {dailyObjectives.map((obj) => (
                <motion.div
                  key={obj.id}
                  whileHover={{ scale: 1.02 }}
                  className={`p-4 rounded-xl border-2 transition-all ${
                    obj.done
                      ? 'bg-green-100 border-green-400'
                      : 'bg-white border-green-200'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        obj.done ? 'bg-green-500' : 'bg-gray-200'
                      }`}>
                        {obj.done ? '✓' : '○'}
                      </div>
                      <span className={`font-bold ${obj.done ? 'line-through text-green-700' : 'text-gray-900'}`}>
                        {obj.label}
                      </span>
                    </div>
                    <span className="text-yellow-600 font-bold">+{obj.points} pts</span>
                  </div>
                </motion.div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Today's Activities - VISUAL */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ delay: 0.2 }}
      >
        <Card className="border-4 border-purple-300 shadow-xl">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-purple-700 mb-4 flex items-center gap-2">
              🗓️ Aujourd'hui
            </h2>
            
            <div className="space-y-3">
              {todayActivities.map((activity, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + idx * 0.1 }}
                  whileHover={{ scale: 1.02, x: 5 }}
                  className={`${activity.color} p-4 rounded-2xl border-2 border-purple-200 cursor-pointer`}
                >
                  <div className="flex items-center gap-4">
                    <motion.div 
                      className="text-5xl"
                      animate={{ rotate: [0, 10, -10, 0] }}
                      transition={{ duration: 2, repeat: Infinity, delay: idx * 0.3 }}
                    >
                      {activity.emoji}
                    </motion.div>
                    <div className="flex-1">
                      <p className="text-xl font-bold text-purple-900">{activity.time} - {activity.title}</p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* My Activities - Gamification */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card className="border-4 border-blue-300 shadow-xl bg-gradient-to-br from-blue-50 to-cyan-50">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-blue-700 mb-4 flex items-center gap-2">
              🎮 Mes Activités
            </h2>

            <div className="grid grid-cols-2 gap-3">
              <GameCard
                icon={Headphones}
                label="Écoute l'histoire"
                points="+10 pts"
                color="bg-purple-500"
                onClick={() => handleGameClick('/audio-kids', 10)}
              />
              <GameCard
                icon={HelpCircle}
                label="Quiz du jour"
                points="+5 pts"
                color="bg-blue-500"
                onClick={() => handleGameClick('/quiz', 5)}
              />
              <GameCard
                icon={Camera}
                label="Photo Challenge"
                points="+10 pts"
                color="bg-pink-500"
                onClick={() => handleGameClick('/photo-challenge', 10)}
              />
              <GameCard
                icon={Trophy}
                label="Mes Badges"
                points={`${badges}/10`}
                color="bg-yellow-500"
                onClick={() => handleGameClick('/badges')}
              />
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Points Progress with animation */}
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.5 }}
      >
        <Card className="border-4 border-yellow-300 shadow-xl bg-gradient-to-br from-yellow-50 to-orange-50">
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-2xl font-bold text-yellow-700 flex items-center gap-2">
                🌟 Mes Points
              </h2>
              <motion.div 
                className="text-4xl font-black text-yellow-600"
                key={points}
                initial={{ scale: 1.5, rotate: 10 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ type: "spring", stiffness: 300 }}
              >
                {points}/100
              </motion.div>
            </div>

            {/* Animated Progress Bar */}
            <div className="h-6 bg-white rounded-full overflow-hidden border-2 border-yellow-300 relative">
              <motion.div
                className="h-full bg-gradient-to-r from-yellow-400 to-orange-400 flex items-center justify-center text-white text-sm font-bold"
                initial={{ width: 0 }}
                animate={{ width: `${points}%` }}
                transition={{ duration: 1, ease: "easeOut" }}
              >
                {points}%
              </motion.div>
              {/* Sparkle effects */}
              {points > 25 && (
                <motion.div
                  className="absolute left-1/4 top-1/2 transform -translate-y-1/2"
                  animate={{ scale: [1, 1.2, 1], opacity: [0.5, 1, 0.5] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                >
                  ✨
                </motion.div>
              )}
            </div>

            <p className="text-center text-yellow-700 mt-3 font-medium">
              Plus que {100 - points} points pour la récompense ! 🎁
            </p>

            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => navigate('/rewards')}
              className="w-full mt-4 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-2xl font-bold text-lg shadow-lg hover:shadow-xl transition-shadow flex items-center justify-center gap-2"
            >
              <Gift className="w-6 h-6" />
              🎁 Voir les Récompenses
            </motion.button>
          </CardContent>
        </Card>
      </motion.div>

      {/* Badges Collection with animations */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
      >
        <Card className="border-4 border-blue-300 shadow-xl">
          <CardContent className="p-6">
            <h2 className="text-2xl font-bold text-blue-700 mb-4">🏆 Collection de Badges</h2>
            
            <div className="grid grid-cols-5 gap-3">
              {[...Array(10)].map((_, idx) => (
                <motion.div
                  key={idx}
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ delay: 0.7 + idx * 0.05, type: "spring" }}
                  whileHover={{ 
                    scale: idx < badges ? 1.2 : 1,
                    rotate: idx < badges ? [0, -10, 10, 0] : 0
                  }}
                  className={`aspect-square rounded-xl flex items-center justify-center text-4xl cursor-pointer ${
                    idx < badges
                      ? 'bg-gradient-to-br from-yellow-400 to-orange-400 shadow-lg'
                      : 'bg-gray-200'
                  }`}
                  onClick={() => {
                    if (idx < badges) {
                      confetti({
                        particleCount: 50,
                        angle: 90,
                        spread: 45,
                        origin: { x: 0.5, y: 0.5 },
                        colors: ['#FFD700', '#FFA500']
                      });
                    }
                  }}
                >
                  {idx < badges ? (
                    <motion.span
                      animate={{ rotate: [0, 10, -10, 0] }}
                      transition={{ duration: 2, repeat: Infinity, delay: idx * 0.2 }}
                    >
                      {getBadgeEmoji(idx)}
                    </motion.span>
                  ) : (
                    '🔒'
                  )}
                </motion.div>
              ))}
            </div>

            <p className="text-center text-blue-600 mt-3 font-medium">
              {badges}/10 badges débloqués ! Continue comme ça ! 💪
            </p>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
}

// Helper Components
function GameCard({ icon: Icon, label, points, color, onClick }) {
  return (
    <motion.button
      whileHover={{ scale: 1.05, rotate: 2 }}
      whileTap={{ scale: 0.95 }}
      onClick={onClick}
      className={`${color} text-white p-4 rounded-2xl shadow-lg hover:shadow-xl transition-all relative overflow-hidden`}
    >
      {/* Animated background shimmer */}
      <motion.div
        className="absolute inset-0 bg-white"
        initial={{ x: '-100%', opacity: 0.3 }}
        animate={{ x: '200%' }}
        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
        style={{ transform: 'skewX(-20deg)' }}
      />
      <div className="relative z-10">
        <motion.div
          animate={{ y: [0, -5, 0] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <Icon className="w-8 h-8 mx-auto mb-2" />
        </motion.div>
        <p className="font-bold text-sm mb-1">{label}</p>
        <p className="text-xs opacity-90">{points}</p>
      </div>
    </motion.button>
  );
}

function getBadgeEmoji(index) {
  const badges = ['🌍', '✈️', '🎨', '🍕', '🏰', '🎭', '🌊', '⛰️', '🎪', '🎬'];
  return badges[index] || '🏆';
}
